import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ScrollText, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Phase 19A — VIP Terms Acceptance.
 * Renders a sticky banner across all VIP pages when the current
 * VIP Terms version has not yet been accepted by this VIP.
 *
 * Soft block: the user can navigate everywhere, but referral
 * "actions" should consult `getNeedsReaccept()` (exposed via window)
 * to decide whether to allow new-referral / claim flows.
 */
const VipTermsAcceptanceBanner = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/vip/terms/status`, { withCredentials: true });
      setStatus(data);
    } catch { /* ignore */ }
  };

  const accept = async () => {
    if (!status?.current_version) return;
    setBusy(true);
    try {
      await axios.post(`${API}/vip/terms/accept`, { version: status.current_version }, { withCredentials: true });
      toast.success(`Accepted v${status.current_version}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record acceptance');
    }
    setBusy(false);
  };

  if (!status || !status.needs_reaccept || collapsed) return null;

  const isFirst = !status.accepted_version;
  return (
    <div
      data-testid="vip-terms-banner"
      className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/40 text-white px-4 py-3 flex items-center gap-3 flex-wrap"
    >
      <ScrollText className="w-5 h-5 text-[#D4AF37] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="text-[#D4AF37] font-semibold">
            {isFirst ? 'Welcome — please review the VIP Terms' : `VIP Terms updated to v${status.current_version}`}
          </span>
          <span className="text-[#A3A3A3] ml-2">
            {isFirst
              ? 'New referrals and payouts will unlock once you accept.'
              : `You last accepted v${status.accepted_version}. New referral actions are paused until you re-accept.`}
          </span>
        </p>
      </div>
      <Link
        to="/vip/terms"
        className="text-[#D4AF37] hover:underline text-xs font-semibold uppercase tracking-wide"
      >
        Read terms
      </Link>
      <Button
        data-testid="vip-terms-accept-btn"
        onClick={accept}
        disabled={busy}
        size="sm"
        className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-9"
      >
        <Check className="w-4 h-4 mr-1.5" />
        {busy ? 'Saving…' : `Accept v${status.current_version}`}
      </Button>
      <button
        data-testid="vip-terms-banner-dismiss"
        onClick={() => setCollapsed(true)}
        className="text-[#A3A3A3] hover:text-white"
        title="Dismiss for this session"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default VipTermsAcceptanceBanner;
